import {
  Box,
  Typography,
  TextField,
  Grid2,
  Paper,
  Button,
  Skeleton,
  Select,
  MenuItem,
  FormControl,
  FormLabel,
  FormHelperText,
  CircularProgress,
} from '@mui/material';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { UserCircleIcon, Buildings, MapPin } from '@phosphor-icons/react';
import LayoutWithSidebar from '../components/common/layoutWithSidebar';
import { HeaderContainer } from '../components/dashboard/AgentInfoHeader';
import theme from '../theme';
import { useUser } from '../hooks/useUser';
import { useAlert } from '../hooks/useAlert';
import MessagePopup from '../components/common/MessagePopup';
import { userProfileSchema } from '../schemas';
import {
  useHqFormattedAddress,
  buildHqFallbackAddress,
} from '../hooks/useHqFormattedAddress';
import { commonStyles } from '../utils/styles/inputs';
import ConfirmationModal from '../components/modals/CofirmationModal';
import { upgradeAccountApi } from '../utils/apis';

const selectFieldSx = {
  height: '40px',
  '& .MuiSelect-select': {
    display: 'flex',
    padding: '8px 14px',
    alignItems: 'center',
    borderRadius: '8px',
    border: '1px solid #DCE0E7',
    background: '#FFF',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: 'normal',
    letterSpacing: '-0.14px',
    color: theme.palette.lightGray.text,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .MuiOutlinedInput-root.Mui-error .MuiSelect-select': {
    borderColor: theme.palette.error.main,
  },
};

const accountTextFieldSx = {
  ...commonStyles.textField,
  '& .MuiOutlinedInput-root': {
    ...(commonStyles.textField['& .MuiOutlinedInput-root'] as Record<
      string,
      unknown
    >),
    borderRadius: '8px',
  },
};

const cardSx = {
  p: 3,
  borderRadius: '8px',
  border: '1px solid #DCE0E7',
  bgcolor: '#FFF',
  height: '100%',
};

const sectionTitleSx = {
  fontWeight: 600,
  fontSize: '16px',
  lineHeight: 1.3,
  color: theme.palette.custom.textBlue,
  mb: 2,
  display: 'flex',
  alignItems: 'center',
  gap: 1,
};

/** Same visual language as dashboard sidebar `ListItemIcon` (icon chip). */
const sectionIconWrapperSx = {
  width: 32,
  height: 32,
  flexShrink: 0,
  borderRadius: '4px',
  bgcolor: theme.palette.custom.sidebarIconBg,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.custom.textBlue,
};

function formikErrorToHelperText(error: unknown): string | undefined {
  if (error == null || error === '') return undefined;
  if (typeof error === 'string') return error;
  if (Array.isArray(error)) {
    const strings = error.filter((e): e is string => typeof e === 'string');
    return strings.length > 0 ? strings.join(', ') : undefined;
  }
  return undefined;
}

function ProfileSkeleton() {
  return (
    <Grid2 container spacing={2}>
      {[0, 1, 2].map(i => (
        <Grid2 size={{ xs: 12, md: 6 }} key={i}>
          <Paper elevation={0} sx={cardSx}>
            <Skeleton variant="text" width="40%" height={28} sx={{ mb: 2 }} />
            <Skeleton variant="rounded" height={48} sx={{ mb: 1.5 }} />
            <Skeleton variant="rounded" height={48} sx={{ mb: 1.5 }} />
            <Skeleton variant="rounded" height={48} />
          </Paper>
        </Grid2>
      ))}
    </Grid2>
  );
}

const UserProfile = () => {
  const queryClient = useQueryClient();
  const { alert, setAlert, showAlert } = useAlert();
  const { user, isLoading, error } = useUser();
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const { formattedAddress, isLoadingGeocode } = useHqFormattedAddress(
    user?.hqLocation
  );

  const accountTypeValue =
    user?.accountType === 'prod' || user?.accountType === 'trial'
      ? user.accountType
      : 'trial';

  const formik = useFormik({
    initialValues: {
      name: user?.name ?? '',
      email: user?.email ?? '',
      businessName: user?.businessName ?? '',
      accountType: accountTypeValue,
    },
    validationSchema: userProfileSchema,
    enableReinitialize: true,
    onSubmit: () => {
      /* Save when profile update API is connected */
    },
  });

  const accountTypeHelperText = formik.touched.accountType
    ? formikErrorToHelperText(formik.errors.accountType)
    : undefined;
  const isTrialAccount = accountTypeValue === 'trial';

  const { mutate: upgradeToPro, isPending: isUpgradePending } = useMutation({
    mutationFn: upgradeAccountApi,
    onSuccess: () => {
      showAlert('Account upgraded to pro successfully', 'success');
      setIsUpgradeModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
    onError: (err: any) => {
      showAlert(
        err?.response?.data?.message || 'Failed to upgrade account to pro',
        'error'
      );
    },
  });

  const hq = user?.hqLocation;
  const detailLine = hq
    ? [hq.address_line1, hq.city, hq.state, hq.country, hq.zip_code]
        .filter(p => typeof p === 'string' && p.trim().length > 0)
        .join(' · ')
    : '';

  return (
    <LayoutWithSidebar margin={2}>
      <HeaderContainer
        sx={{
          position: 'sticky',
          top: 0,
          mb: 2,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '18px',
              color: theme.palette.custom.textBlue,
              letterSpacing: '-0.02em',
            }}
          >
            User profile
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Account details from your session.
          </Typography>
        </Box>
        {isTrialAccount && (
          <Button
            type="button"
            variant="contained"
            onClick={() => setIsUpgradeModalOpen(true)}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              px: 2.5,
              boxShadow: 'none',
            }}
          >
            Upgrade to Pro
          </Button>
        )}
      </HeaderContainer>

      {isLoading && <ProfileSkeleton />}

      {!isLoading && error && (
        <Paper
          elevation={0}
          sx={{ ...cardSx, borderColor: theme.palette.error.light }}
        >
          <Typography color="error">
            We could not load your profile. Please refresh or try again later.
          </Typography>
        </Paper>
      )}

      {!isLoading && !error && user && (
        <Box component="form" onSubmit={formik.handleSubmit} noValidate>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Paper elevation={0} sx={cardSx}>
                <Typography component="h2" sx={sectionTitleSx}>
                  <Box sx={sectionIconWrapperSx} aria-hidden>
                    <UserCircleIcon size={20} weight="regular" />
                  </Box>
                  Account
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                  }}
                >
                  <FormControl fullWidth>
                    <FormLabel htmlFor="name" sx={commonStyles.formLabel}>
                      Full name
                    </FormLabel>
                    <TextField
                      fullWidth
                      id="name"
                      name="name"
                      placeholder="Full name"
                      value={formik.values.name}
                      disabled
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.name && Boolean(formik.errors.name)}
                      helperText={
                        formik.touched.name
                          ? formikErrorToHelperText(formik.errors.name)
                          : undefined
                      }
                      sx={accountTextFieldSx}
                    />
                  </FormControl>
                  <FormControl fullWidth>
                    <FormLabel htmlFor="email" sx={commonStyles.formLabel}>
                      Email
                    </FormLabel>
                    <TextField
                      fullWidth
                      id="email"
                      name="email"
                      placeholder="Email"
                      type="email"
                      value={formik.values.email}
                      disabled
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.email && Boolean(formik.errors.email)
                      }
                      helperText={
                        formik.touched.email
                          ? formikErrorToHelperText(formik.errors.email)
                          : undefined
                      }
                      sx={accountTextFieldSx}
                    />
                  </FormControl>
                  <FormControl fullWidth>
                    <FormLabel
                      htmlFor="businessName"
                      sx={commonStyles.formLabel}
                    >
                      Business name
                    </FormLabel>
                    <TextField
                      fullWidth
                      id="businessName"
                      name="businessName"
                      placeholder="Business name"
                      value={formik.values.businessName}
                      disabled
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.businessName &&
                        Boolean(formik.errors.businessName)
                      }
                      helperText={
                        formik.touched.businessName
                          ? formikErrorToHelperText(formik.errors.businessName)
                          : undefined
                      }
                      sx={accountTextFieldSx}
                    />
                  </FormControl>
                  <FormControl
                    fullWidth
                    error={
                      formik.touched.accountType &&
                      Boolean(formik.errors.accountType)
                    }
                  >
                    <FormLabel
                      htmlFor="accountType"
                      sx={commonStyles.formLabel}
                    >
                      Account type
                    </FormLabel>
                    <Select
                      id="accountType"
                      name="accountType"
                      value={formik.values.accountType}
                      disabled
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      sx={selectFieldSx}
                      inputProps={{ 'aria-label': 'Account type' }}
                    >
                      <MenuItem value="trial">Trial</MenuItem>
                      <MenuItem value="prod">Production</MenuItem>
                    </Select>
                    {accountTypeHelperText && (
                      <FormHelperText>{accountTypeHelperText}</FormHelperText>
                    )}
                  </FormControl>
                </Box>
              </Paper>
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }}>
              <Paper elevation={0} sx={cardSx}>
                <Typography component="h2" sx={sectionTitleSx}>
                  <Box sx={sectionIconWrapperSx} aria-hidden>
                    <Buildings size={20} weight="regular" />
                  </Box>
                  Organization
                </Typography>
                {user.organization ? (
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Organization name
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 500,
                          color: theme.palette.text.primary,
                          fontSize: '15px',
                        }}
                      >
                        {user.organization.organizationName}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Organization code
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 500,
                          fontSize: '15px',
                          letterSpacing: '0.04em',
                        }}
                      >
                        {user.organization.organizationCode}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Your role
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 500,
                          fontSize: '15px',
                          textTransform: 'capitalize',
                        }}
                      >
                        {user.organization.role}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Organization ID
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontFamily: 'ui-monospace, monospace',
                          color: theme.palette.text.secondary,
                          wordBreak: 'break-all',
                        }}
                      >
                        {user.organization.organizationId}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Typography color="text.secondary">
                    No organization is linked to this account.
                  </Typography>
                )}
              </Paper>
            </Grid2>

            <Grid2 size={{ xs: 12 }}>
              <Paper elevation={0} sx={cardSx}>
                <Typography component="h2" sx={sectionTitleSx}>
                  <Box sx={sectionIconWrapperSx} aria-hidden>
                    <MapPin size={20} weight="regular" />
                  </Box>
                  Headquarters address
                </Typography>
                {!hq ? (
                  <Typography color="text.secondary">
                    No headquarters location on file.
                  </Typography>
                ) : (
                  <Box
                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
                  >
                    {isLoadingGeocode && (
                      <CircularProgress size={20} sx={{ mt: 0.5 }} />
                    )}
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 500,
                          fontSize: '15px',
                          color: theme.palette.text.primary,
                          lineHeight: 1.5,
                        }}
                      >
                        {isLoadingGeocode
                          ? 'Resolving address…'
                          : formattedAddress}
                      </Typography>
                      {detailLine && !isLoadingGeocode && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1, lineHeight: 1.5 }}
                        >
                          {detailLine}
                        </Typography>
                      )}
                      {!isLoadingGeocode &&
                        buildHqFallbackAddress(hq) !== formattedAddress && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 1 }}
                          >
                            Formatted via location lookup from coordinates.
                          </Typography>
                        )}
                    </Box>
                  </Box>
                )}
              </Paper>
            </Grid2>
          </Grid2>
        </Box>
      )}
      <ConfirmationModal
        open={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        onConfirm={() => upgradeToPro()}
        isPending={isUpgradePending}
        title="Upgrade to pro"
        description="You are about to upgrade your account to pro."
        confirmButtonText="Confirm"
        cancelButtonText="Decline"
        confirmButtonColor="primary"
      />
      <MessagePopup alert={alert} setAlert={setAlert} />
    </LayoutWithSidebar>
  );
};

export default UserProfile;
